import React, { useState } from 'react';
import { checkVoucher, redeemVoucher, reactivateVoucher } from '../api';

const ManualCodeEntry = ({ token, currentUser }) => {
  const [codeInput, setCodeInput] = useState('BOHO-');
  const [voucherData, setVoucherData] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [reactivating, setReactivating] = useState(false);
  const [newValidUntil, setNewValidUntil] = useState('');
  const [useAmount, setUseAmount] = useState('');
  const [reactivationAmount, setReactivationAmount] = useState('');
  const [processing, setProcessing] = useState(false);

  // Sprawdź czy voucher jest kwotowy (można odejmować częściowe kwoty)
  const isAmountVoucher = (productName) => {
    if (!productName) return false;
    return productName.toLowerCase().includes('voucher kwotowy');
  };

  // Sprawdź czy użytkownik ma uprawnienia do edycji kwoty (admin lub kierownik)
  const canEditAmount = () => {
    if (!currentUser) return false;
    return ['admin', 'kierownik'].includes(currentUser.role);
  };

  const formatCurrency = (value) => {
    const numeric = Number.parseFloat(value);
    if (!Number.isFinite(numeric)) {
      return '-';
    }
    return numeric.toFixed(2);
  };

  const formatAmountInput = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const number = Number.parseFloat(value);
    if (!Number.isFinite(number)) return '';
    return number.toFixed(2);
  };

  const parseAmountInput = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const normalized = String(value).replace(',', '.');
    const number = Number.parseFloat(normalized);
    if (!Number.isFinite(number)) return null;
    return Math.round(number * 100) / 100;
  };

  const initializeAmounts = (data) => {
    const defaultRemaining = data?.remainingValue ?? data?.productPrice ?? '';
    const baseProduct = data?.status === 'active'
      ? data?.remainingValue ?? data?.productPrice ?? ''
      : data?.productPrice ?? data?.remainingValue ?? '';
    setUseAmount(data?.status === 'active' ? formatAmountInput(defaultRemaining) : '');
    setReactivationAmount(formatAmountInput(baseProduct));
  };

  const previewRemainingAfterUse = () => {
    if (voucherData?.remainingValue === undefined) return null;
    const current = Number.parseFloat(voucherData.remainingValue);
    if (!Number.isFinite(current)) return null;
    const usage = parseAmountInput(useAmount) ?? 0;
    const diff = Math.max(0, Math.round((current - usage) * 100) / 100);
    return diff.toFixed(2);
  };

  const handleCheckCode = async () => {
    if (!codeInput.startsWith('BOHO-') || codeInput.length <= 5) {
      setErrorMessage('Kod musi zaczynać się od BOHO- i mieć więcej znaków.');
      return;
    }
    setErrorMessage('');
    setSuccessMessage('');
    setVoucherData(null);
    setProcessing(true);
    try {
      const response = await checkVoucher(token, codeInput);
      setVoucherData(response.data);
      initializeAmounts(response.data);
    } catch (err) {
      console.error('Error checking voucher by code:', err);
      setErrorMessage(`Błąd podczas sprawdzania vouchera: ${err.response?.data?.error || err.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const handleUseVoucher = async () => {
    if (voucherData && voucherData.voucherId) {
      const parsedAmount = parseAmountInput(useAmount);
      if (parsedAmount === null || parsedAmount <= 0) {
        setErrorMessage('Podaj prawidłową kwotę większą od zera.');
        return;
      }
      setProcessing(true);
      setErrorMessage('');
      setSuccessMessage('');
      try {
        const response = await redeemVoucher(token, voucherData.voucherId, parsedAmount);
        const refreshed = await checkVoucher(token, codeInput);
        setVoucherData(refreshed.data);
        initializeAmounts(refreshed.data);
        setSuccessMessage(response.data.message);
      } catch (err) {
        console.error('Error redeeming voucher:', err);
        setErrorMessage(`Błąd podczas wykorzystywania vouchera: ${err.response?.data?.error || err.message}`);
      } finally {
        setProcessing(false);
      }
    }
  };

  // Wykorzystaj całość vouchera (pełna kwota remaining_value)
  const handleUseVoucherFull = async () => {
    if (voucherData && voucherData.voucherId) {
      const fullAmount = parseAmountInput(voucherData.remainingValue);
      if (fullAmount === null || fullAmount <= 0) {
        setErrorMessage('Voucher nie ma dostępnej kwoty do wykorzystania.');
        return;
      }
      setProcessing(true);
      setErrorMessage('');
      setSuccessMessage('');
      try {
        const response = await redeemVoucher(token, voucherData.voucherId, fullAmount);
        const refreshed = await checkVoucher(token, codeInput);
        setVoucherData(refreshed.data);
        initializeAmounts(refreshed.data);
        setSuccessMessage(response.data.message);
      } catch (err) {
        console.error('Error redeeming voucher:', err);
        setErrorMessage(`Błąd podczas wykorzystywania vouchera: ${err.response?.data?.error || err.message}`);
      } finally {
        setProcessing(false);
      }
    }
  };

  const handleReactivateVoucher = async () => {
    if (voucherData && voucherData.voucherId) {
      setReactivating(true);
      try {
        let response;
        const parsedAmount = parseAmountInput(reactivationAmount);
        if (parsedAmount === null || parsedAmount <= 0) {
          setErrorMessage('Podaj prawidłową kwotę reaktywacji większą od zera.');
          setReactivating(false);
          return;
        }
        setErrorMessage('');
        setSuccessMessage('');
        if (voucherData.status === 'expired') {
          if (!newValidUntil) {
            setErrorMessage('Musisz podać nową datę ważności dla wygasłego vouchera.');
            setReactivating(false);
            return;
          }
          response = await reactivateVoucher(token, voucherData.voucherId, newValidUntil, parsedAmount);
        } else {
          response = await reactivateVoucher(token, voucherData.voucherId, null, parsedAmount);
        }
        const refreshed = await checkVoucher(token, codeInput);
        setVoucherData(refreshed.data);
        initializeAmounts(refreshed.data);
        setNewValidUntil('');
        setSuccessMessage(response.data.message);
      } catch (err) {
        console.error('Error reactivating voucher:', err);
        setErrorMessage(`Błąd podczas reaktywacji vouchera: ${err.response?.data?.error || err.message}`);
      } finally {
        setReactivating(false);
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800 border-green-200';
      case 'used': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'expired': return 'bg-red-100 text-red-800 border-red-200';
      case 'inactive': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return 'Aktywny';
      case 'used': return 'Wykorzystany';
      case 'expired': return 'Wygasły';
      case 'inactive': return 'Nieaktywny';
      default: return status;
    }
  };

  const remainingPreview = previewRemainingAfterUse();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="container mx-auto px-4 py-8 max-w-md">
        
        {/* Header */}
        <div className="mb-8 pt-12">
          
          
          <div className="text-center">
            <div className="bg-gradient-to-r from-green-600 to-teal-600 w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Wprowadź kod</h2>
            <p className="text-gray-600 text-sm">Wpisz kod vouchera ręcznie</p>
          </div>
        </div>

        {/* Code Input Form */}
        {!voucherData && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Kod vouchera
                </label>
                <input 
                  type="text" 
                  inputMode="decimal"
                  value={codeInput} 
                  onChange={(e) => setCodeInput(e.target.value)} 
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent text-lg font-mono"
                  placeholder="BOHO-XXXXXX"
                />
              </div>
              <button 
                onClick={handleCheckCode} 
                disabled={processing}
                className="w-full bg-green-600 text-white py-4 px-6 rounded-xl font-semibold text-lg shadow-lg active:scale-95 transition-all duration-150 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {processing ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Sprawdzanie...</span>
                  </div>
                ) : (
                  'Sprawdź kod'
                )}
              </button>
            </div>
          </div>
        )}

        {/* Error Message */}
        {errorMessage && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-2xl p-4">
            <div className="flex items-start space-x-3">
              <svg className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-red-700 text-sm">{errorMessage}</p>
            </div>
          </div>
        )}

        {successMessage && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-2xl p-4">
            <div className="flex items-start space-x-3">
              <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <p className="text-green-700 text-sm">{successMessage}</p>
            </div>
          </div>
        )}

        {/* Voucher Data */}
        {voucherData && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="p-6">
              {/* Status Badge */}
              <div className="flex items-center justify-center mb-6">
                <span className={`px-4 py-2 rounded-full text-sm font-semibold border ${getStatusColor(voucherData.status)}`}>
                  {getStatusText(voucherData.status)}
                </span>
              </div>

              {/* Voucher Details */}
              <div className="space-y-6 mb-6">
                {/* Code Display */}
                <div className="text-center">
                  <p className="text-xs text-gray-500 mb-1">Kod vouchera</p>
                  <p className="font-mono text-lg font-semibold text-gray-800">{codeInput}</p>
                </div>

                {/* Customer & Product Info */}
                <div className="grid grid-cols-1 gap-4 text-sm">
                  {/* Customer Info */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-700 mb-3">Informacje o kupującym</h4>
                    <div className="space-y-2">
                      {voucherData.fullName && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Imię i nazwisko:</span>
                          <span className="font-medium text-right">{voucherData.fullName}</span>
                        </div>
                      )}
                      {voucherData.email && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Email:</span>
                          <span className="font-medium text-right break-all">{voucherData.email}</span>
                        </div>
                      )}
                      {voucherData.phone && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Telefon:</span>
                          <span className="font-medium text-right">{voucherData.phone}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Product Info */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-700 mb-3">Szczegóły produktu</h4>
                    <div className="space-y-2">
                      {voucherData.productName && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Produkt:</span>
                          <span className="font-medium text-right">{voucherData.productName}</span>
                        </div>
                      )}
                      {voucherData.productPrice && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Wartość:</span>
                          <span className="font-medium text-right">{formatCurrency(voucherData.productPrice)} zł</span>
                        </div>
                      )}
                      {typeof voucherData.remainingValue !== 'undefined' && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Pozostała wartość:</span>
                          <span className="font-medium text-right">{formatCurrency(voucherData.remainingValue)} zł</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Dates Info */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-700 mb-3">Daty i terminy</h4>
                    <div className="space-y-2">
                      {voucherData.purchaseDate && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Data zakupu:</span>
                          <span className="font-medium text-right">{new Date(voucherData.purchaseDate).toLocaleDateString('pl-PL')}</span>
                        </div>
                      )}
                      {voucherData.validUntil && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Ważny do:</span>
                          <span className="font-medium text-right">{new Date(voucherData.validUntil).toLocaleDateString('pl-PL')}</span>
                        </div>
                      )}
                      {voucherData.redeemedAt && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Wykorzystany:</span>
                          <span className="font-medium text-right">{new Date(voucherData.redeemedAt).toLocaleString('pl-PL')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Message */}
              <div className="text-center mb-6 p-3 bg-blue-50 rounded-lg">
                <p className="text-blue-800 font-medium">{voucherData.message}</p>
              </div>

              {/* Actions based on status */}
              {voucherData.status === 'active' && (
                <div className="space-y-4">
                  {/* Ostrzeżenie dla admina/kierownika przy nie-voucherze kwotowym */}
                  {!isAmountVoucher(voucherData.productName) && canEditAmount() && (
                    <div className="bg-orange-50 border border-orange-200 rounded-xl p-3">
                      <div className="flex items-start space-x-2">
                        <span className="text-orange-500 flex-shrink-0">⚠️</span>
                        <p className="text-sm text-orange-700">
                          <strong>UWAGA:</strong> To nie jest voucher kwotowy. Jego kwota nie powinna być zmieniana - voucher powinien być wykorzystany w całości.
                        </p>
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Kwota do wykorzystania
                      {!isAmountVoucher(voucherData.productName) && !canEditAmount() && (
                        <span className="ml-2 text-xs text-gray-500">(tylko pełna kwota)</span>
                      )}
                    </label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={useAmount}
                      onChange={(e) => setUseAmount(e.target.value)}
                      className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:border-transparent ${
                        !isAmountVoucher(voucherData.productName) && !canEditAmount()
                          ? 'border-gray-200 bg-gray-100 text-gray-500 cursor-not-allowed'
                          : 'border-gray-300 focus:ring-green-500'
                      }`}
                      placeholder="np. 150.00"
                      disabled={processing || (!isAmountVoucher(voucherData.productName) && !canEditAmount())}
                      readOnly={!isAmountVoucher(voucherData.productName) && !canEditAmount()}
                    />
                    {remainingPreview !== null && (
                      <p className="text-xs text-gray-500 mt-1">
                        Pozostanie {formatCurrency(remainingPreview)} zł po transakcji.
                      </p>
                    )}
                    {!isAmountVoucher(voucherData.productName) && !canEditAmount() && (
                      <p className="text-xs text-orange-600 mt-1">
                        Ten voucher można wykorzystać tylko w całości. Edycja kwoty jest zablokowana.
                      </p>
                    )}
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Aktualnie dostępne:</span>
                      <span className="font-semibold text-gray-800">{formatCurrency(voucherData.remainingValue)} zł</span>
                    </div>
                    {remainingPreview !== null && (
                      <div className="flex justify-between mt-2">
                        <span className="text-gray-600">Po odliczeniu:</span>
                        <span className="font-semibold text-gray-800">{formatCurrency(remainingPreview)} zł</span>
                      </div>
                    )}
                  </div>
                  <div className={`grid gap-3 ${isAmountVoucher(voucherData.productName) || canEditAmount() ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}>
                    {/* Przycisk "Odlicz kwotę" - tylko dla voucherów kwotowych lub admin/kierownik */}
                    {(isAmountVoucher(voucherData.productName) || canEditAmount()) && (
                      <button 
                        onClick={handleUseVoucher} 
                        disabled={processing}
                        className="w-full bg-green-600 text-white py-4 px-6 rounded-xl font-semibold shadow-lg active:scale-95 transition-all duration-150 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {processing ? (
                          <div className="flex items-center justify-center space-x-2">
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            <span>Przetwarzanie...</span>
                          </div>
                        ) : (
                          'Odlicz kwotę'
                        )}
                      </button>
                    )}
                    <button 
                      onClick={handleUseVoucherFull} 
                      disabled={processing}
                      className="w-full bg-blue-600 text-white py-4 px-6 rounded-xl font-semibold shadow-lg active:scale-95 transition-all duration-150 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {processing ? (
                        <div className="flex items-center justify-center space-x-2">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          <span>Przetwarzanie...</span>
                        </div>
                      ) : (
                        'Wykorzystaj całość'
                      )}
                    </button>
                  </div>
                </div>
              )}

              {(voucherData.status === 'used' || voucherData.status === 'inactive' || voucherData.status === 'expired') && (
                <div className="space-y-4">
                  {voucherData.status === 'expired' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nowa data ważności:
                      </label>
                      <input
                        type="date"
                        value={newValidUntil}
                        onChange={(e) => setNewValidUntil(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nowa kwota vouchera
                    </label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={reactivationAmount}
                      onChange={(e) => setReactivationAmount(e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="np. 200.00"
                      disabled={reactivating}
                    />
                  </div>
                  <button 
                    onClick={handleReactivateVoucher} 
                    disabled={reactivating}
                    className="w-full bg-orange-600 text-white py-4 px-6 rounded-xl font-semibold shadow-lg active:scale-95 transition-all duration-150 hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {reactivating ? (
                      <div className="flex items-center justify-center space-x-2">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        <span>Reaktywowanie...</span>
                      </div>
                    ) : (
                      'Reaktywuj voucher'
                    )}
                  </button>
                </div>
              )}

              {/* Check Another Code Button */}
              <div className="mt-4 pt-4 border-t border-gray-100">
                <button 
                  onClick={() => {
                    setVoucherData(null);
                    setCodeInput('BOHO-');
                    setErrorMessage('');
                    setNewValidUntil('');
                    setSuccessMessage('');
                    setUseAmount('');
                    setReactivationAmount('');
                  }}
                  className="w-full bg-gray-100 text-gray-700 py-3 px-6 rounded-xl font-medium active:scale-95 transition-all duration-150 hover:bg-gray-200"
                >
                  Sprawdź inny kod
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManualCodeEntry;
